/*
 * Description:
 * ---------------------
 * ObfuscationPanel — "who can see what", in plain language.
 *
 * THE SHAPE OF THE SCREEN
 *   The answer first: three cards saying what an outsider actually learns.
 *   Then the two settings that change that answer — how strongly the tunnel
 *   conceals, and how much of your traffic is on it. Then, collapsed, the
 *   measurements and the individual mechanisms.
 *
 *   Strength is a LADDER, not five switches. The appliance refuses to spread
 *   on a readable wire and refuses to un-conceal the wire underneath live
 *   spreading, so a control that can be rejected is a control the UI should
 *   not offer in that state. Picking a rung applies whatever sequence of calls
 *   the interlocks require, in the order they require it.
 *
 * THE HONESTY RULE
 *   Every rung states its cost beside what it hides, and the live section
 *   reports the REALISED per-link split, not the configured one — the share a
 *   wiretap actually captured. A mechanism that is armed but reaching none of
 *   this site's traffic says so, loudly, at the top.
 *
 * ! file ObfuscationPanel.js
 * ---------------
 * \author: Tolga O. Atalay
 * \Affiliation: A2 Labs LLC / A12XT Inc.
 * \date: 2025
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FiSlash, FiEyeOff, FiShuffle, FiCloud, FiGlobe, FiCheck, FiChevronDown,
  FiAlertTriangle, FiRefreshCw, FiInfo, FiActivity,
} from "react-icons/fi";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { fmtDuration, fmtBitRate } from "../utils/formatters";
import { useToast } from "./ToastProvider";
import { useConfirm } from "./ConfirmModal";

/* ── The ladder ──────────────────────────────────────────────────────────────
   Cumulative on purpose: every rung includes everything below it. That is what
   lets one control replace six switches whose legal combinations an operator
   would otherwise have to memorise. */

const LEVELS = ["off", "concealed", "unlinkable", "covered", "dispersed"];

const LEVEL = {
  off: {
    icon: FiSlash, color: "#94A3B8", label: "Off",
    short: "A normal encrypted tunnel",
    adds: "Traffic is encrypted, but the tunnel names itself on the network and each flow stays on one link.",
    cost: "Nothing.",
    state: { wire: false, pad: false, mode: "off", chaff: false, jitter: false, anchors: false },
  },
  concealed: {
    icon: FiEyeOff, color: "#10B981", label: "Concealed",
    short: "Stops identifying itself",
    adds: "Removes the markers that say what this is — the signature bytes, the version, which link a packet took, and the counter tying them together. Packets are padded to standard sizes so lengths stop naming the application.",
    cost: "About 5 bytes per packet, plus padding. No delay.",
    state: { wire: true, pad: true, mode: "off", chaff: false, jitter: false, anchors: false },
  },
  unlinkable: {
    icon: FiShuffle, color: "#3B82F6", label: "Unlinkable",
    short: "No one link sees the whole conversation",
    adds: "Every packet takes an unpredictable link, so a watcher on any one of them captures a fraction and cannot stitch it back together with what another link saw. The far end restores the order.",
    cost: "Adds reorder delay, and paces the flow toward the slowest link.",
    state: { wire: true, pad: true, mode: "uniform", chaff: false, jitter: false, anchors: false },
  },
  covered: {
    icon: FiCloud, color: "#8B5CF6", label: "Covered",
    short: "Stops showing when you are active",
    adds: "Steady cover traffic fills the quiet gaps and each packet leaves at a randomised moment, so the timing and volume of the link stop following what people are doing.",
    cost: "Uses bandwidth continuously and adds a few milliseconds.",
    state: { wire: true, pad: true, mode: "uniform", chaff: true, jitter: true, anchors: false },
  },
  dispersed: {
    icon: FiGlobe, color: "#F59E0B", label: "Dispersed",
    short: "No one exit sees all of it",
    adds: "Each connection leaves through a different exit point, drawn unpredictably. This defends what the rungs below cannot: they hide traffic from a watcher on your own links, this removes the single exit that would otherwise see everything after it leaves the tunnel.",
    cost: "Needs two or more reachable exits. A connection keeps its exit until it closes.",
    state: { wire: true, pad: true, mode: "uniform", chaff: true, jitter: true, anchors: true },
  },
};

/* Which field of a rung's state governs each mechanism. Absent from this map
   (fixed rate, automatic re-keying) means the mechanism belongs to no rung and
   is always the operator's own choice. */
const MECH_FIELD = {
  wire: "wire", pad: "pad", blend: "mode", chaff: "chaff",
  jitter: "jitter", anchors: "anchors",
};

const MECH_LABEL = {
  wire: "hiding the protocol", pad: "size padding", blend: "spreading across links",
  chaff: "cover traffic", jitter: "timing randomisation", anchors: "spreading across exits",
};

/* ── ARMED vs EFFECTIVE ──────────────────────────────────────────────────────
   Every mechanism here lives in the userspace encrypted lane. The BPF fast path
   branches to that lane at one line — `if (policy->enc_mode != 0)` — and
   everything below it is the plaintext path: a kernel-chosen interface, a v1
   NABX header naming the protocol, a link id, a walking counter, one anchor.

   So a class with no suite armed is untouched by every switch below. An
   operator could step the whole ladder, watch each control light up, and be
   concealing nothing but the cover traffic.

   Two mechanisms differ, and the difference matters:
     chaff   — the lane MANUFACTURES cover, so it works the moment it is armed.
               It conceals the lane's activity, never the site's.
     anchors — the placer only sees connections that pass through the lane, and
               cover is not placed across exits. With nothing encrypted it is
               inert, not partial.

   demo/theater/static/shared.js carries the same table for the demo panel.
   Change one, change the other — they are the same claim. */
const SCOPE = {
  wire:    { needsEnc: true,  inert: "your packets still carry a readable protocol header" },
  pad:     { needsEnc: true,  inert: "your packets keep their natural sizes" },
  blend:   { needsEnc: true,  inert: "each of your flows stays pinned to one link by the kernel" },
  chaff:   { needsEnc: false, inert: "" },
  jitter:  { needsEnc: true,  inert: "your packets leave on their natural timing" },
  anchors: { needsEnc: true,  inert: "every one of your flows surfaces at the single active exit — only the cover traffic reaches both, and unweighted" },
};

/* Defaults when a rung turns a control on. Deliberately modest: this panel
   should never be the reason a link saturates. All adjustable below. */
const CHAFF_FLOOR_DEFAULT = 100;  // packets/sec
const JITTER_MS_DEFAULT = 5;
const BIAS_DEFAULT = 2.0;      // matches balanceMaxBiasDefault on the edge
const MTD_PERIOD_DEFAULT = 300;   // 5 min: longer than any capture needed to
                                  // infer one schedule, short enough that the
                                  // model expires within a session.

/* The five distributions, in the same words wherever they appear — they are
   the same five, and two names would imply two features. */
const PATTERNS = [
  ["constant", "Steady", "One exact number, forever. Hides the most, and a perfectly flat line is itself unusual."],
  ["uniform", "Even spread", "Any value in the range, equally likely. Each step unrelated to the last."],
  ["lognormal", "Natural", "Mostly mid-range with gentle swings — closest to a real link's load."],
  ["pareto", "Bursty", "Usually near the bottom, with occasional big jumps. Real traffic behaves like this."],
  ["walk", "Drifting", "Wanders slowly, each step near the last. Looks most like a link in use."],
];

const fmtUs = (us) => {
  if (us == null || !isFinite(us)) return "—";
  return us >= 1000 ? `${(us / 1000).toFixed(1)} ms` : `${Math.round(us)} µs`;
};
const fmtCount = (n) => (n == null || !isFinite(n) ? "—" : Number(n).toLocaleString());
const mbitOf = (pps, size) => (pps * size * 8) / 1e6;
const fmtRate = (m) => fmtBitRate(m, { style: "bit/s" });

/* What the appliance is doing, in the same shape as a rung's state. */
const liveState = (st) => ({
  wire: !!st?.obf_wire,
  pad: !!st?.obf_pad,
  mode: st?.spread?.mode || "off",
  chaff: !!st?.chaff,
  jitter: !!st?.jitter,
  anchors: !!st?.anchor_spread,
});

const sameState = (a, b) =>
  Object.keys(MECH_FIELD).every((m) => a[MECH_FIELD[m]] === b[MECH_FIELD[m]]);

/* The rung the live state corresponds to. Anything matching none is "custom",
   reported as such rather than rounded — showing a rung the appliance is not
   in is the specific failure this panel exists to avoid. */
function inferLevel(st) {
  if (!st) return "off";
  const cur = liveState(st);
  return LEVELS.find((k) => sameState(cur, LEVEL[k].state)) || "custom";
}

/* When it matches none, name the closest and say exactly what differs.
   "Custom" alone is a dead end: off the map, with no way back. */
function nearestLevel(st) {
  const cur = liveState(st);
  let best = null;
  for (const key of LEVELS) {
    const want = LEVEL[key].state;
    const diffs = Object.keys(MECH_FIELD).filter((m) => cur[MECH_FIELD[m]] !== want[MECH_FIELD[m]]);
    if (!best || diffs.length < best.diffs.length) best = { key, diffs };
  }
  return best;
}

/* Is a mechanism acting on the OPERATOR'S traffic, as opposed to merely armed?
   `anchors` takes a measurement rather than a flag: armed and keyed still
   places nothing until connections reach the placer. anchor_flows is the live
   count; anchor_draws is cumulative for the process and never resets, so
   gating on that alone claims a split hours after the last flow ended. */
function mechLive(mech, on, st) {
  if (!on) return false;
  if (mech === "anchors") {
    return (st?.anchor_flows || 0) > 0 && (st?.anchor_effective_n || 0) >= 1.5;
  }
  const spec = SCOPE[mech];
  return !spec?.needsEnc || (st?.enc_classes_armed || 0) > 0;
}

function inertMechs(st) {
  const cur = liveState(st);
  const on = {
    wire: cur.wire, pad: cur.pad, blend: cur.mode !== "off",
    chaff: cur.chaff, jitter: cur.jitter, anchors: cur.anchors,
  };
  return Object.keys(on).filter((m) => on[m] && !mechLive(m, on[m], st));
}

/* Arming exit spreading with nothing encrypted is not refused — it is a legal
   state, and arming the tier before the classes is a reasonable order to work
   in. But it is worth saying BEFORE the click and not only in the inert banner
   after it: "armed and keyed" reads as working, and this is the one rung that is
   inert rather than partial with no coverage, because cover traffic is not
   placed across exits either. */
const ANCHORS_NO_COVERAGE_CONFIRM = {
  title: "Spread exits with nothing encrypted?",
  message:
    "Exit spreading only places connections that travel the encrypted path, and no traffic " +
    "class is armed for encryption right now.\n\n" +
    "It will be armed and keyed, and it will carry none of your traffic — every flow keeps " +
    "surfacing at the single active exit. Arm a class below to make it take effect.",
  confirmLabel: "Arm it anyway",
  variant: "primary",
};

export default function ObfuscationPanel({ nodeApiUrl, onNavigate }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [showMeasure, setShowMeasure] = useState(false);
  const [showMech, setShowMech] = useState(false);

  const [chaffFloor, setChaffFloor] = useState(CHAFF_FLOOR_DEFAULT);
  const [jitterMs, setJitterMs] = useState(JITTER_MS_DEFAULT);
  const [mtdPeriod, setMtdPeriod] = useState(MTD_PERIOD_DEFAULT);
  const [bias, setBias] = useState(BIAS_DEFAULT);
  const [coverSpread, setCoverSpread] = useState(40);
  const [coverPeriod, setCoverPeriod] = useState(10);
  const [strictRate, setStrictRate] = useState(300);
  const [strictSize, setStrictSize] = useState(1024);
  const [strictShape, setStrictShape] = useState("constant");
  const [strictSpread, setStrictSpread] = useState(40);
  const [strictPeriod, setStrictPeriod] = useState(20);
  const [rateUnit, setRateUnit] = useState("Mbit");
  /* Per-exit weights as percentages. Null = even, which is the
     obfuscation-correct default and what the backend does with no weights. */
  const [weights, setWeights] = useState(null);

  /* Live split of the fixed rate, differenced between polls. strict_cover_share
     is cumulative since the lane started, so after an hour of idling it sits
     near 100% whatever is happening now — the bar would stand still while the
     link changed underneath it, and the whole point is that it moves. */
  const [flow, setFlow] = useState(null);
  const [history, setHistory] = useState([]);
  const lastFlow = useRef(null);

  /* Fields the operator has edited but not applied. The poll re-seeds every
     input from the appliance, which is right for a value nobody is touching and
     destructive for one being typed into. A ref, because marking a field dirty
     must not re-render the input being typed in. */
  const dirty = useRef(new Set());
  const touch = (k) => dirty.current.add(k);
  const settle = (k) => dirty.current.delete(k);

  const toast = useToast();
  const confirm = useConfirm();

  const url = useCallback((p) => (nodeApiUrl ? nodeApiUrl(p) : null), [nodeApiUrl]);

  const load = useCallback(async () => {
    const u = url("/obf/status");
    if (!u) return;
    try {
      const r = await fetchWithTimeout(u, {}, 8000);
      if (!r.ok) throw new Error(`status ${r.status}`);
      const j = await r.json();
      setStatus(j);
      setErr("");

      const seed = (key, ok, set) => { if (ok && !dirty.current.has(key)) set(); };
      seed("chaffFloor", j.chaff_floor_pps > 0, () => setChaffFloor(j.chaff_floor_pps));
      seed("jitterMs", j.jitter_max_ms > 0, () => setJitterMs(j.jitter_max_ms));
      seed("mtdPeriod", j.mtd_period_sec > 0, () => setMtdPeriod(j.mtd_period_sec));
      seed("bias", j.anchor_balance_bias > 0, () => setBias(j.anchor_balance_bias));
      seed("coverSpread", j.cover_spread > 0, () => setCoverSpread(Math.round(j.cover_spread * 100)));
      seed("coverPeriod", j.cover_period_sec > 0, () => setCoverPeriod(j.cover_period_sec));
      seed("strictRate", j.strict_rate_pps > 0, () => setStrictRate(j.strict_rate_pps));
      /* Floored at the appliance's minimum: an older build reported a random
         padding-ladder rung when no size was set, seeding 64 into a field whose
         every submission was then refused for being under 512. */
      seed("strictSize", j.strict_size_bytes >= 512, () => setStrictSize(j.strict_size_bytes));
      seed("strictShape", !!j.strict_shape, () => setStrictShape(j.strict_shape));
      seed("strictSpread", j.strict_spread > 0, () => setStrictSpread(Math.round(j.strict_spread * 100)));
      seed("strictPeriod", j.strict_period_sec > 0, () => setStrictPeriod(j.strict_period_sec));

      const now = Date.now();
      const prev = lastFlow.current;
      const cur = { real: j.strict_real_sent || 0, cover: j.strict_cover_sent || 0, t: now };
      if (prev && j.strict && cur.t > prev.t) {
        const dt = (cur.t - prev.t) / 1000;
        const dr = Math.max(0, cur.real - prev.real) / dt;
        const dc = Math.max(0, cur.cover - prev.cover) / dt;
        if (dr + dc > 0) {
          setFlow({ real: dr, cover: dc });
          setHistory((h) => [...h, dr + dc].slice(-40));
        }
      } else if (!j.strict) {
        setFlow(null);
        setHistory([]);
      }
      lastFlow.current = cur;
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [url]);

  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [load]);

  /* One control change. The appliance's refusals explain themselves, so they
     are surfaced verbatim — an operator told only "failed" will just retry. */
  const post = useCallback(async (path, body) => {
    const u = url(path);
    if (!u) return;
    const r = await fetchWithTimeout(u, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }, 10000);
    if (!r.ok) {
      let msg = `HTTP ${r.status}`;
      const j = await r.json().catch(() => null);
      if (j?.error) msg = j.error;
      throw new Error(msg);
    }
  }, [url]);

  /* Raw single-mechanism change, for the individual controls. */
  const raw = useCallback(async (path, body, label) => {
    ["chaffFloor", "jitterMs", "mtdPeriod", "coverSpread", "coverPeriod",
     "strictRate", "strictSize", "strictSpread", "strictPeriod"].forEach(settle);
    setBusy(true);
    try {
      await post(path, body);
      toast.success(label);
    } catch (e) {
      toast.error(String(e.message || e));
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
      load();
    }
  }, [post, toast, load]);

  /* Apply a rung as an ordered sequence. The order is not cosmetic: the
     appliance refuses to spread on a readable wire, and refuses to reveal the
     wire underneath live spreading or cover. So everything depending on the
     wire is torn down before the wire changes, and rebuilt after. */
  /* One gate, both arming paths — the rung below and the mechanism toggle. */
  const confirmAnchorCoverage = useCallback(async () => {
    if ((status?.enc_classes_armed || 0) > 0) return true;
    return confirm(ANCHORS_NO_COVERAGE_CONFIRM);
  }, [confirm, status]);

  const applyLevel = useCallback(async (key) => {
    const target = LEVEL[key]?.state;
    if (!target) return;

    /* Asked whenever the change turns cover traffic on, whichever rung does it. */
    if (target.chaff && !status?.chaff) {
      const ok = await confirm({
        title: "Turn on cover traffic?",
        message: `The link will keep sending filler at roughly ${chaffFloor} packets per second whether or not anyone is using it — that is what makes it cover. Timing randomisation adds up to ${jitterMs} ms to each packet.`,
        confirmLabel: "Turn it on",
        variant: "primary",
      });
      if (!ok) return;
    }

    if (target.anchors && !(await confirmAnchorCoverage())) return;

    setBusy(true);
    try {
      // 1. tear down what depends on the wire
      if (!target.anchors) await post("/obf/anchors", { enabled: false });
      if (!target.chaff) await post("/obf/chaff", { enabled: false, floor_pps: 0 });
      if (!target.jitter) await post("/obf/jitter", { enabled: false, max_ms: 0 });
      if (target.mode === "off") await post("/obf/blend", { mode: "off" });
      // 2. the wire itself, now unblocked in either direction
      await post("/obf/wire", { enabled: target.wire, pad: target.pad });
      // 3. build back up
      if (target.mode !== "off") await post("/obf/blend", { mode: target.mode });
      if (target.chaff) await post("/obf/chaff", { enabled: true, floor_pps: chaffFloor });
      if (target.jitter) await post("/obf/jitter", { enabled: true, max_ms: jitterMs });
      if (target.anchors) await post("/obf/anchors", { enabled: true });
      toast.success(`Protection set to ${LEVEL[key].label}`);
      setErr("");
    } catch (e) {
      toast.error(String(e.message || e));
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
      load();
    }
  }, [post, confirm, confirmAnchorCoverage, toast, load, chaffFloor, jitterMs, status]);

  /* Coverage — the second half of the model.
     A rung configures the LANE. Traffic reaches that lane only when a class is
     armed for encryption, so what an outsider meets is STRENGTH × COVERAGE, and
     a zero in either makes the other worth nothing. */
  const setCoverage = useCallback(async (suite) => {
    const u = url("/policy/encryption/all");
    if (!u) return;
    setBusy(true);
    setErr("");
    try {
      const r = await fetchWithTimeout(u, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ suite }),
      }, 20000);
      if (!r.ok) {
        let msg = `HTTP ${r.status}`;
        const j = await r.json().catch(() => null);
        if (j?.error) msg = j.error;
        throw new Error(msg);
      }
      await load();
    } catch (e) {
      setErr(String(e.message || e));
      toast.error(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }, [url, load, toast]);

  /* Putting every class on the lane moves the whole site off the in-kernel fast
     path. On a busy edge that is a throughput decision, not a display one —
     measured at thousands of drops within minutes — so it is stated before it
     happens rather than discovered afterwards. */
  const coverAll = useCallback(async () => {
    const ok = await confirm({
      title: "Put all traffic on the concealed path?",
      message:
        "Concealment only applies to traffic on the encrypted path, so this is what makes the level above apply to your own traffic.\n\n" +
        "It also moves every class off the in-kernel fast path into the userspace lane. On a busy link that costs throughput and adds reordering — and the management path to this appliance runs through the same tunnel.\n\n" +
        "On a quiet link, or for a demonstration, this is the right choice. Under load, pick classes individually on the Encryption panel.",
      confirmLabel: "Put everything on it",
      variant: "primary",
    });
    if (ok) await setCoverage("strong");
  }, [confirm, setCoverage]);

  const applyWeights = useCallback(async (w) => {
    setBusy(true);
    try {
      await post("/obf/anchors/weights", { weights: w || {} });
      toast.success(w ? "Exit balance applied" : "Even split restored");
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setBusy(false);
      load();
    }
  }, [post, toast, load]);

  const applyCoverShape = useCallback(async (shape) => {
    setBusy(true);
    try {
      await post("/obf/chaff/shape", {
        shape,
        spread: shape === "constant" ? 0 : coverSpread / 100,
        period_sec: shape === "constant" ? 0 : coverPeriod,
      });
      settle("coverSpread"); settle("coverPeriod");
      toast.success(shape === "constant" ? "Cover rate held steady" : "Cover rate now varies");
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setBusy(false);
      load();
    }
  }, [post, toast, load, coverSpread, coverPeriod]);

  const applyStrictShape = useCallback((shape) => {
    settle("strictSpread"); settle("strictPeriod");
    return raw("/obf/strict/shape",
      { shape, spread: strictSpread / 100, period_sec: strictPeriod },
      "Rate pattern updated");
  }, [raw, strictSpread, strictPeriod]);

  /* ── Derived state ────────────────────────────────────────────────────── */

  const level = inferLevel(status);
  const near = level === "custom" ? nearestLevel(status) : null;

  /* Which mechanisms are actually on. The ladder can only say "no named level";
     this says what IS running, which is the half an operator reads the panel
     for and the half that was missing from a custom state. */
  const runningMechs = (() => {
    const cur = liveState(status);
    return Object.keys(MECH_FIELD).filter((mm) =>
      MECH_FIELD[mm] === "mode" ? cur.mode !== "off" : Boolean(cur[MECH_FIELD[mm]])
    );
  })();
  const cur = liveState(status);

  const spread = status?.spread || {};
  // Memoised: a fresh {} each render would re-run every useMemo keyed on it.
  const draws = useMemo(() => status?.spread?.draws || {}, [status]);
  const reasm = status?.reassembly || {};
  const totalDraws = useMemo(() => Object.values(draws).reduce((a, b) => a + b, 0), [draws]);
  const topShare = useMemo(
    () => (totalDraws ? Math.max(...Object.values(draws)) / totalDraws : 0),
    [draws, totalDraws]);

  /* Field names come from /obf/status, the only endpoint this panel polls.
     `ready` and `effective_anchors` live on /obf/anchors instead, and reading
     those here rendered an empty lever while every value was one endpoint
     away. */
  const readyList = useMemo(() => status?.anchor_ready_ids || [], [status]);
  const readyCount = readyList.length;
  const appliedWeights = status?.anchor_weights || null;

  /* Diversity by VOLUME, not by flow count. A count treats a keepalive and a
     4 GB transfer as one flow each, so three exits holding one flow apiece read
     as perfect diversity while the exit with the transfer saw the whole site.
     Lead with the byte measure and fall back to the count only before any
     traffic exists to weigh. */
  const effByCount = status?.anchor_effective_n ?? readyCount;
  const effAnchors = status?.anchor_effective_bytes_n || effByCount;
  /* Flows older than one rotation period keep their exit until they close, so
     this is the ceiling on what re-drawing can achieve. */
  const frozenFlows = status?.anchor_long_flows || 0;
  const balancing = !!status?.anchor_balancing;
  /* An exit whose traffic is almost all cover is an exit where an observer reads
     the cover shape directly — the cost of a non-uniform split. */
  const coverOnlyShare = status?.anchor_cover_share_max || 0;
  /* Bounds and cost come from the edge, so the slider cannot offer a setting the
     appliance refuses or misstate what the current one costs. */
  const biasMin = status?.anchor_balance_bias_min ?? 1;
  const biasMax = status?.anchor_balance_bias_max ?? 4;
  const biasRatio = status?.anchor_balance_share_ratio ?? 1;

  const wireOn = !!status?.obf_wire;
  const padOn = !!status?.obf_pad;
  const blended = (spread.mode || "off") !== "off";
  const covered = !!status?.chaff;
  const strictOn = !!status?.strict;
  const jitterOn = !!status?.jitter;
  const anchorsOn = !!status?.anchor_spread;
  /* The appliance refuses exit spreading with fewer than two exits holding keys,
     so neither the Dispersed rung nor the exit switch offers it then. */
  const exitsBlocked = !anchorsOn && readyCount < 2 ? "needs 2 reachable exits" : null;
  const mtdOn = !!status?.mtd;

  const armedClasses = status?.enc_classes_armed || 0;
  const onLane = armedClasses > 0;
  const inert = inertMechs(status);
  const timingHidden = covered || strictOn;

  const capMbit = mbitOf(strictRate, strictSize);
  const shaped = strictShape !== "constant";
  const capMax = shaped ? capMbit * (1 + strictSpread / 100) : capMbit;
  const liveMbit = flow ? ((flow.real + flow.cover) * strictSize * 8) / 1e6 : null;
  const yourShare = flow
    ? Math.min(1, flow.real / Math.max(1, flow.real + flow.cover))
    : status?.strict ? 0 : 0.28;

  /* Drawn against the full range, not its own min/max: autoscaling would make a
     dead-flat Steady line look as dramatic as a Bursty one. */
  const spark = useMemo(() => {
    if (history.length < 2) return null;
    const top = Math.max(capMax * 1.05, ...history.map((v) => (v * strictSize * 8) / 1e6));
    return history.map((v, i) => {
      const x = (i / (history.length - 1)) * 100;
      const y = 26 - ((v * strictSize * 8) / 1e6 / top) * 24;
      return `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(" ");
  }, [history, capMax, strictSize]);

  const weightPct = (id) => {
    if (weights && weights[id] != null) return weights[id];
    if (appliedWeights && appliedWeights[id] != null) {
      const t = Object.values(appliedWeights).reduce((a, b) => a + b, 0) || 1;
      return Math.round((appliedWeights[id] / t) * 100);
    }
    return readyCount ? Math.round(100 / readyCount) : 0;
  };

  /* Moving one slider takes its share FROM THE OTHERS, proportionally, so the
     set always totals exactly 100. Independent sliders looked right and lied:
     the label said "%" while the values were relative weights, so 45/33/33 was
     normalised to 40.5/29.7/29.7 and the operator got a split they never asked
     for. A control that reads as a percentage should behave like one. */
  const setWeight = (id, v) => {
    setWeights((prev) => {
      const base = prev || Object.fromEntries(readyList.map((a) => [a, weightPct(a)]));
      const others = readyList.filter((a) => a !== id);
      const val = Math.max(0, Math.min(100, Math.round(v)));
      if (!others.length) return { [id]: 100 };

      const remaining = 100 - val;
      const otherTotal = others.reduce((s, a) => s + (base[a] || 0), 0);
      const share = {};
      others.forEach((a) => {
        share[a] = otherTotal > 0
          ? ((base[a] || 0) / otherTotal) * remaining
          : remaining / others.length;
      });

      // Integer percentages that still total 100: floor everything, then give
      // the leftover to the largest fractional parts.
      const next = { [id]: val };
      let used = 0;
      const rems = others.map((a) => {
        const f = Math.floor(share[a]);
        next[a] = f;
        used += f;
        return { a, frac: share[a] - f };
      });
      rems.sort((x, y) => y.frac - x.frac);
      for (let i = 0; used < remaining && i < rems.length; i++, used++) next[rems[i].a] += 1;
      return next;
    });
  };

  /* What the PENDING split would be worth, so the cost moves while the slider
     does. effAnchors is realised — measured from flows already placed — and
     would sit still until Apply and then traffic, making the lever feel inert
     at exactly the moment the operator is judging the trade. */
  const pendingEff = useMemo(() => {
    if (!weights) return null;
    const vals = Object.values(weights).filter((v) => v > 0);
    const total = vals.reduce((a, b) => a + b, 0);
    if (!total) return null;
    const sumSq = vals.reduce((a, v) => a + (v / total) ** 2, 0);
    return sumSq > 0 ? 1 / sumSq : null;
  }, [weights]);

  /* ── The three watchers ───────────────────────────────────────────────────
     Coverage gates every verdict. These describe what an outsider learns about
     THIS SITE'S TRAFFIC; with no class on the encrypted path there is no such
     traffic to conceal, so the honest answer to all three is "everything",
     whatever the switches say. Reading the mechanisms alone produced "sees
     noise" over a link carrying the site's conversations in the clear. */
  const watchers = onLane ? [
    {
      who: "On one of your links",
      grade: !wireOn ? "bad" : !blended ? "warn" : !timingHidden ? "warn" : "good",
      verdict: !wireOn ? "Sees everything"
        : !blended ? "Sees whole conversations"
        : !timingHidden ? "Sees when you are active"
        : "Sees noise",
      sub: !wireOn
        ? "The tunnel names itself in every packet, and each flow stays on one link."
        : !blended
          ? "Traffic is unmarked, but every flow stays on one link start to finish."
          : !timingHidden
            ? `Unmarked and split — the busiest link carries about ${Math.round((topShare || 0) * 100) || 0}% — but it still goes quiet when nobody is working.`
            : strictOn
              ? "Unmarked, split across links, held at a fixed rate. Nothing in the shape to read."
              : "Unmarked, split across links, and covered so the quiet periods are filled.",
    },
    {
      who: "Where you come out",
      grade: !anchorsOn || readyCount < 2 ? "bad" : effAnchors < readyCount - 0.5 ? "warn" : "good",
      verdict: !anchorsOn || readyCount < 2 ? "Sees all of it"
        : effAnchors < readyCount - 0.5 ? "Sees most of it"
        : `Sees about ${Math.round(100 / Math.max(1, effAnchors))}%`,
      sub: !anchorsOn
        ? "Every connection surfaces at one exit, so whoever watches it sees the whole site."
        : readyCount < 2
          ? `Spreading is on but only ${readyCount} exit is reachable, so there is nothing to spread across.`
          : [
              `${readyCount} exits reachable, behaving like ${effAnchors.toFixed(1)} by volume.`,
              effAnchors < readyCount - 0.5 ? "The balance favours one of them." : "Evenly balanced.",
              /* The bound on re-drawing: a flow that outlived a rotation keeps its
                 exit until it closes, so it keeps showing that exit the same
                 conversation however often the draw is re-keyed. */
              frozenFlows > 0
                ? `${frozenFlows} connection${frozenFlows > 1 ? "s have" : " has"} outlived a re-draw and cannot move until ${frozenFlows > 1 ? "they close" : "it closes"}.`
                : "",
              /* Cover is drawn evenly while real flows follow the split, so a
                 lightly-used exit sees almost pure cover — a shaped signal. */
              coverOnlyShare > 0.9
                ? "One exit is carrying almost nothing but cover, which is a shaped signal an observer there can characterise."
                : "",
            ].filter(Boolean).join(" "),
    },
    {
      who: "Watching for months",
      grade: !wireOn ? "bad" : mtdOn ? "good" : "warn",
      verdict: !wireOn ? "Builds a full picture" : mtdOn ? "Model keeps expiring" : "Can build a model",
      sub: !wireOn
        ? "With the protocol visible there is nothing to learn — it is readable already."
        : mtdOn
          ? `Schedules re-key on a randomised interval${
              status?.mtd_rotations > 0 ? `, ${fmtCount(status.mtd_rotations)} times so far` : ""
            }, so anything inferred goes stale.`
          : "The link and exit schedules have not changed since this lane started. Given long enough, they can be learned.",
    },
  ] : [
    {
      who: "On one of your links",
      grade: "bad",
      verdict: "Sees everything",
      sub: "No traffic class is encrypted, so every packet takes the in-kernel fast path: a header that names the tunnel, a link id, and a counter tying the links together.",
    },
    {
      who: "Where you come out",
      grade: "bad",
      verdict: "Sees all of it",
      sub: anchorsOn
        ? "Exit spreading is armed and keyed, but it only places connections that reach the encrypted path. Every one of your flows surfaces at the single active exit."
        : "Every connection surfaces at one exit, so whoever watches it sees the whole site.",
    },
    {
      who: "Watching for months",
      grade: "bad",
      verdict: "Builds a full picture",
      sub: "With your traffic on the readable path there is nothing to infer — it is legible already.",
    },
  ];

  /* ── Render ───────────────────────────────────────────────────────────── */

  if (loading) {
    return <div className="tp-page"><div className="tp-card tp-loading">Reading the concealment settings…</div></div>;
  }

  if (status?.available === false) {
    return (
      <div className="tp-page">
        <div className="tp-card tp-empty">
          <FiInfo size={22} />
          <div>
            <h3>Concealment is not available on this edge</h3>
            <p>{status.reason || "The encrypted lane is not running on this node."}</p>
            <p>Concealment works on encrypted traffic, so at least one traffic class has to be
               encrypted first.</p>
            {onNavigate && (
              <button className="tp-btn tp-btn--primary" onClick={() => onNavigate("encryption")}>
                Open the Encryption panel
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const rung = LEVELS.indexOf(level);

  /* A custom state is not a blank one. indexOf("custom") is -1, which used to
     leave the whole ladder unlit — no fill, no marked rung — reading as "every
     layer is off" while two of them were running and one change stood between
     here and a named rung. So the ladder anchors on the nearest rung and stops
     just short of it: honest that nothing is reached, clear about where you
     are. */
  const nearIdx = near ? LEVELS.indexOf(near.key) : -1;
  const fillPct = rung > 0
    ? (rung / (LEVELS.length - 1)) * 100
    : nearIdx > 0 ? ((nearIdx - 0.45) / (LEVELS.length - 1)) * 100 : 0;

  return (
    <div className="tp-page">

      {err && <Alert kind="danger" title="That change did not apply">{err}</Alert>}

      {/* Armed, but not on your traffic. Every control below arms a mechanism in
          the encrypted lane, and traffic enters that lane only when a class is
          armed for encryption — so with none armed an operator can step the
          entire ladder, watch every switch light up, and conceal nothing but
          the cover traffic. */}
      {inert.length > 0 && (
        <div className="tp-alert tp-alert--warn">
          <FiAlertTriangle size={16} />
          <div>
            <b>
              {inert.length} setting{inert.length > 1 ? "s are" : " is"} switched on
              but not protecting your traffic
            </b>
            <div>
              {inert.map((m) => MECH_LABEL[m]).join(", ")} —{" "}
              {!onLane
                ? "no traffic class is encrypted, so your packets stay on the in-kernel fast path and never reach any of it."
                : "armed and keyed, but nothing has been placed through it yet."}
              {SCOPE[inert[0]]?.inert ? ` Right now, ${SCOPE[inert[0]].inert}.` : ""}
            </div>
            <div className="tp-alert-acts">
              {onNavigate && (
                <button className="tp-btn tp-btn--primary" onClick={() => onNavigate("encryption")}>
                  Choose what to encrypt
                </button>
              )}
              <button className="tp-btn" disabled={busy} onClick={() => applyLevel("off")}>
                Switch these off
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── 1. The answer ─────────────────────────────────────────────────── */}
      <section className="tp-card">
        <div className="tp-card-head">
          <h3>What an outsider can work out</h3>
          <span className="tp-badge" style={{ "--c": LEVEL[level]?.color || "#F59E0B" }}>
            {level === "custom" ? "Custom" : LEVEL[level].label}
          </span>
        </div>

        <div className="tp-scores">
          {watchers.map((w) => (
            <div key={w.who} className="tp-score">
              <div className="tp-score-who">{w.who}</div>
              <div className="tp-score-verdict">
                <i className={`tp-pip ${w.grade}`} />{w.verdict}
              </div>
              <div className="tp-score-sub">{w.sub}</div>
            </div>
          ))}
        </div>

        {/* The wire held flat — the product in one picture: real traffic moves,
            the total does not. */}
        {strictOn && (
          <div className="tp-wire">
            <div className="tp-wire-head">
              <span>On the link right now</span>
              <span className="tp-wire-rate">
                {fmtRate(liveMbit != null ? liveMbit : capMbit)}{shaped ? " — patterned" : " — flat"}
              </span>
            </div>
            <div className="tp-wire-bar">
              <div className="tp-wire-real" style={{ width: `${(yourShare * 100).toFixed(1)}%` }} />
              <div className="tp-wire-fill" style={{ width: `${(100 - yourShare * 100).toFixed(1)}%` }} />
            </div>
            <div className="tp-wire-key">
              <span><i className="tp-sw real" /> your traffic <b>{fmtRate(yourShare * capMbit)}</b></span>
              <span><i className="tp-sw fill" /> filler <b>{fmtRate((1 - yourShare) * capMbit)}</b></span>
              <span className="tp-wire-eq">the total never moves</span>
            </div>
          </div>
        )}
      </section>

      {/* ── 2. Step one: strength ─────────────────────────────────────────── */}
      <section className="tp-card">
        <StepHead n={1} title="Choose how much to conceal"
                  sub="Each step includes everything before it, and states what it costs." />

        <div className="tp-ladder">
          <div className="tp-ladder-track">
            <div className="tp-ladder-fill" style={{ width: `${fillPct}%` }} />
          </div>
          {LEVELS.map((key, i) => {
            const m = LEVEL[key];
            const Icon = m.icon;
            const on = level === key;
            const passed = rung >= 0 && i < rung;
            // Nearest rung when off-ladder: outlined rather than filled, because
            // you are close to it, not on it.
            const nearHere = nearIdx === i && level === "custom";
            const blocked = m.state.anchors ? exitsBlocked : null;
            return (
              <button key={key} type="button"
                      className={`tp-rung${on ? " is-on" : ""}${passed ? " is-passed" : ""}${nearHere ? " is-near" : ""}${blocked ? " is-blocked" : ""}`}
                      style={{ "--c": m.color }}
                      disabled={busy || !!blocked} title={blocked || undefined}
                      onClick={() => applyLevel(key)}>
                <span className="tp-rung-dot">
                  {passed ? <FiCheck size={13} /> : <Icon size={14} />}
                </span>
                <span className="tp-rung-label">{m.label}</span>
                <span className="tp-rung-short">{m.short}</span>
                {nearHere && (
                  <span className="tp-rung-near">
                    {near.diffs.length} change{near.diffs.length === 1 ? "" : "s"} away
                  </span>
                )}
                {blocked && <span className="tp-rung-blocked">{blocked}</span>}
              </button>
            );
          })}
        </div>

        {level !== "custom" ? (
          <div className="tp-detail-box" style={{ "--c": LEVEL[level].color }}>
            <p>{LEVEL[level].adds}</p>
            <p className="tp-detail-cost"><FiActivity size={12} /> Cost: {LEVEL[level].cost}</p>
          </div>
        ) : (
          <div className="tp-detail-box tp-detail-box--custom">
            <p>
              <FiInfo size={13} /> These settings match no step. Closest is{" "}
              <strong>{LEVEL[near.key].label}</strong>, with {near.diffs.length}{" "}
              change{near.diffs.length === 1 ? "" : "s"}:{" "}
              {near.diffs.map((d, i) => (
                <span key={d}>
                  {i > 0 && (i === near.diffs.length - 1 ? " and " : ", ")}
                  <strong>
                    {MECH_LABEL[d]}{" "}
                    {(MECH_FIELD[d] === "mode" ? cur.mode !== "off" : cur[MECH_FIELD[d]]) ? "on" : "off"}
                  </strong>
                </span>
              ))}.
            </p>
            <p className="tp-detail-running">
              {runningMechs.length
                ? <>Running now: {runningMechs.map((mm) => MECH_LABEL[mm]).join(", ")}.</>
                : <>Nothing is concealing anything right now.</>}
            </p>
            <button className="tp-btn" disabled={busy} onClick={() => applyLevel(near.key)}>
              <FiRefreshCw size={12} /> Go back to {LEVEL[near.key].label}
            </button>
          </div>
        )}
      </section>

      {/* ── 3. Step two: coverage ─────────────────────────────────────────── */}
      <section className="tp-card">
        <StepHead n={2} title="Choose how much of your traffic gets it"
                  sub="Concealment applies only to encrypted traffic. Everything else takes the in-kernel fast path, where none of step 1 reaches it." />

        <div className={`tp-coverage${onLane ? " is-on" : " is-gap"}`}>
          <div>
            <b>
              {onLane
                ? `${fmtCount(armedClasses)} traffic ${armedClasses === 1 ? "class is" : "classes are"} on the concealed path`
                : "No traffic is on the concealed path"}
            </b>
            <span>
              {onLane
                ? "Anything not encrypted still takes the fast path in the clear."
                : "Step 1 is running — on the cover traffic. None of it reaches a packet this site sent."}
            </span>
          </div>
          <div className="tp-coverage-acts">
            <button className={`tp-btn${onLane ? "" : " tp-btn--primary"}`}
                    disabled={busy} onClick={coverAll}>
              Put everything on it
            </button>
            {onNavigate && (
              <button className="tp-btn" disabled={busy} onClick={() => onNavigate("encryption")}>
                Choose per class
              </button>
            )}
            {onLane && (
              <button className="tp-btn" disabled={busy} onClick={() => setCoverage("off")}>
                Take it all off
              </button>
            )}
          </div>
        </div>

        <div className={`tp-summary tp-summary--${onLane ? (level === "off" ? "none" : "on") : "gap"}`}>
          <b>In effect right now</b>
          <p>
            {level === "off" && !onLane &&
              "No concealment, and nothing on the concealed path. Traffic runs as a standard encrypted tunnel that identifies itself."}
            {level === "off" && onLane &&
              "Traffic is on the concealed path, but step 1 is Off — so there is nothing for it to conceal. Raise the level above."}
            {level !== "off" && !onLane && (
              <>
                <strong>{level === "custom" ? "A custom level" : LEVEL[level].label}</strong>{" "}
                is running, applied to <strong>none of your traffic</strong>. Only the cover
                traffic is concealed; every packet this site sends takes the fast path in the
                clear. Set coverage above to make it real.
              </>
            )}
            {level !== "off" && onLane && (
              <>
                <strong>{level === "custom" ? "A custom level" : LEVEL[level].label}</strong>,
                applied to <strong>{fmtCount(armedClasses)} traffic{" "}
                {armedClasses === 1 ? "class" : "classes"}</strong>.{" "}
                {level === "dispersed" && (status?.anchor_flows || 0) === 0
                  ? "Exit spreading has not placed a connection yet — it will as soon as one of those classes opens a flow."
                  : "That traffic gets everything the step describes; anything not covered does not."}
              </>
            )}
          </p>
        </div>
      </section>

      {/* ── 4. Measurements ───────────────────────────────────────────────── */}
      <section className="tp-card">
        <button type="button" className="tp-disclose" onClick={() => setShowMeasure((v) => !v)}>
          <FiChevronDown size={14} className={showMeasure ? "rot" : ""} />
          What it actually measured
          {(topShare > 0.7 || reasm.late > 0) && <span className="tp-disclose-flag">needs a look</span>}
        </button>

        {showMeasure && (
          <div className="tp-measure">
            {!blended ? (
              <p className="tp-muted">
                Spreading across links is off, so there is no split to measure yet.
              </p>
            ) : totalDraws === 0 ? (
              <p className="tp-muted">Spreading is on, but no traffic has gone through it yet.</p>
            ) : (
              <>
                <div className="tp-measure-label">
                  What each link actually carried — measured, not configured
                </div>
                <div className="tp-bars">
                  {Object.entries(draws).sort((a, b) => Number(a[0]) - Number(b[0])).map(([pid, n]) => {
                    const pct = (100 * n) / totalDraws;
                    return (
                      <Bar key={pid} name={`Link ${pid}`} pct={pct} hot={pct > 70} />
                    );
                  })}
                </div>
                {topShare > 0.7 && (
                  <Note kind="warn">
                    One link is carrying most of the traffic, so a watcher there has effectively
                    the whole conversation. That is aggregation, not concealment — set the split
                    to equal shares in the individual controls below.
                  </Note>
                )}
              </>
            )}

            <div className="tp-stats">
              <Stat label="Added delay, typical" value={fmtUs(reasm.hold_p50_us)} />
              <Stat label="Added delay, worst 1%" value={fmtUs(reasm.hold_p99_us)} />
              <Stat label="Packets given up on" value={fmtCount(reasm.skipped_packets)}
                    warn={reasm.skipped_packets > 0} />
              <Stat label="Arrived too late" value={fmtCount(reasm.late)} warn={reasm.late > 0} />
              {covered && <Stat label="Cover packets sent" value={fmtCount(status?.tx_chaff)} />}
              {anchorsOn && <Stat label="Connections spread over exits" value={fmtCount(status?.anchor_flows)} />}
            </div>

            {reasm.late > 0 && (
              <Note kind="warn">
                Packets are arriving after their place was given up, and are being discarded.
                Your links differ in speed by more than the reorder budget allows.
              </Note>
            )}

            {anchorsOn && status?.anchor_draws && Object.keys(status.anchor_draws).length > 0 && (
              <>
                <div className="tp-measure-label">Connections per exit point</div>
                <div className="tp-bars">
                  {Object.entries(status.anchor_draws).sort(([a], [b]) => a.localeCompare(b))
                    .map(([anchor, n]) => {
                      const t = Object.values(status.anchor_draws).reduce((s, v) => s + v, 0) || 1;
                      return <Bar key={anchor} name={anchor} pct={(n / t) * 100} mono />;
                    })}
                </div>
              </>
            )}
          </div>
        )}
      </section>

      {/* ── 5. Individual controls ────────────────────────────────────────── */}
      <section className="tp-card">
        {/* The individual controls live behind this disclosure, so a walkthrough
            pointing at one finds nothing until it is open. Listing what it
            reveals lets the walkthrough open it itself rather than telling the
            operator a control is "not on screen" while they are looking at the
            panel that holds it. */}
        <button type="button" className="tp-disclose"
                data-tour-opens="obf.wire obf.pad obf.blend obf.chaff obf.jitter obf.anchors obf.strict obf.rotation"
                onClick={() => setShowMech((v) => !v)}>
          <FiChevronDown size={14} className={showMech ? "rot" : ""} />
          Individual controls — set each mechanism by hand
        </button>

        {showMech && (
          <div className="tp-mechs">
            <p className="tp-muted tp-mechs-intro">
              Everything in step 1 is one of these. Changing one by hand moves the level
              above to “Custom”.
            </p>

            <Mech
              tour="obf.wire" name="Hide the protocol" what="Strips the bytes that identify this tunnel, and hides the packet counter."
              on={wireOn} busy={busy}
              costs={[["+5 B per packet"], ["no delay"]]}
              live={wireOn ? `${fmtCount(status?.obf_tags)} keys live` : "off"}
              onToggle={() => raw("/obf/wire", { enabled: !wireOn, pad: padOn },
                wireOn ? "Protocol visible again" : "Protocol hidden")}
            />

            <Mech
              tour="obf.pad" name="Pad packet sizes" what="Rounds every packet to a standard size, so its length stops naming the app."
              on={padOn} busy={busy}
              blocked={!wireOn ? "needs the protocol hidden" : null}
              costs={[["up to +100 B per packet"], ["no delay"]]}
              live={padOn ? "9 standard sizes" : "off"}
              onToggle={() => raw("/obf/wire", { enabled: true, pad: !padOn },
                padOn ? "Padding off" : "Padding on")}
            />

            <Mech
              tour="obf.blend" name="Spread across links" what="Sends each packet down an unpredictable link, so no one link sees the conversation."
              on={blended} busy={busy}
              blocked={!wireOn ? "needs the protocol hidden" : null}
              costs={[["reorder delay", "warn"], ["paced to the slowest link", "warn"]]}
              live={blended ? (totalDraws > 0 ? `busiest link ${Math.round(topShare * 100)}%` : "no traffic yet") : "off"}
              liveWarn={topShare > 0.7}
              onToggle={() => raw("/obf/blend", { mode: blended ? "off" : "uniform" },
                blended ? "Spreading off" : "Spreading on")}
              action={blended && (
                <button className="tp-btn tp-btn--sm" disabled={busy}
                        onClick={() => raw("/obf/rotate", {}, "New spread key in use")}>
                  <FiRefreshCw size={12} /> New spread key
                  {spread.epoch > 0 && <em>{fmtCount(spread.epoch)} so far</em>}
                </button>
              )}
            >
              <Field label="How the links share the load">
                <div className="tp-seg">
                  {[["uniform", "Equal shares", "Every link carries the same amount — this is the concealing option"],
                    ["weighted", "By capacity", "Keeps combined speed, but the fastest link carries most of the traffic"]]
                    .map(([key, label, hint]) => (
                      <button key={key} title={hint}
                              className={`tp-seg-btn${(spread.mode || "off") === key ? " is-on" : ""}`}
                              disabled={busy || !wireOn}
                              onClick={() => raw("/obf/blend", { mode: key }, `Split: ${label}`)}>
                        {label}
                      </button>
                    ))}
                </div>
              </Field>
              {(spread.mode || "off") === "weighted" && (
                <Note kind="warn">
                  By capacity keeps your combined speed but leaks the ratio: the fastest link
                  carries most of every flow, so a watcher there sees nearly the whole
                  conversation. It hides the order, not the content.
                </Note>
              )}
            </Mech>

            <Mech
              tour="obf.chaff" name="Cover traffic" what="Fills the quiet gaps, so the link stops going idle when nobody is working."
              on={covered} busy={busy}
              blocked={!wireOn ? "needs the protocol hidden" : null}
              costs={[[`${chaffFloor} pkt/s floor`, "warn"], ["bandwidth only"]]}
              live={covered
                ? status?.cover_exceeded_pct != null
                  ? `you outran it ${status.cover_exceeded_pct.toFixed(0)}% of the time`
                  : `holding ${status?.cover_target_pps || chaffFloor} pkt/s`
                : "off"}
              liveWarn={status?.cover_exceeded_pct > 20}
              onToggle={() => raw("/obf/chaff", { enabled: !covered, floor_pps: chaffFloor },
                covered ? "Cover traffic off" : "Cover traffic on")}
            >
              <Field label="Keep the link at least">
                <input type="number" min="1" max="2000" value={chaffFloor}
                       onChange={(e) => { touch("chaffFloor"); setChaffFloor(Math.max(1, +e.target.value || 1)); }} />
                <span className="tp-unit">packets/sec</span>
                <button className="tp-btn tp-btn--sm" disabled={busy || !wireOn || !covered}
                        onClick={() => raw("/obf/chaff", { enabled: true, floor_pps: chaffFloor }, "Cover level updated")}>
                  Apply
                </button>
              </Field>

              <Field label="Rate pattern">
                <div className="tp-chips">
                  {PATTERNS.map(([key, label, why]) => (
                    <button key={key} title={why}
                            className={`tp-chip${(status?.cover_shape || "constant") === key ? " is-on" : ""}`}
                            disabled={busy || !wireOn} onClick={() => applyCoverShape(key)}>
                      {label}
                    </button>
                  ))}
                </div>
              </Field>

              {(status?.cover_shape || "constant") !== "constant" && (
                <>
                  <Field label="How much it varies">
                    <input type="range" min="5" max="100" step="5" value={coverSpread}
                           disabled={busy || !wireOn}
                           onChange={(e) => { touch("coverSpread"); setCoverSpread(+e.target.value); }}
                           onMouseUp={() => applyCoverShape(status?.cover_shape || "uniform")}
                           onTouchEnd={() => applyCoverShape(status?.cover_shape || "uniform")} />
                    <span className="tp-val">{coverSpread}%</span>
                  </Field>
                  <Field label="Pick a new rate every">
                    <input type="number" min="2" max="600" value={coverPeriod}
                           onChange={(e) => { touch("coverPeriod"); setCoverPeriod(Math.min(600, Math.max(2, +e.target.value || 2))); }} />
                    <span className="tp-unit">seconds</span>
                    <button className="tp-btn tp-btn--sm" disabled={busy || !wireOn}
                            onClick={() => applyCoverShape(status?.cover_shape || "uniform")}>Apply</button>
                  </Field>
                </>
              )}

              {status?.cover_exceeded_pct > 20 && (
                <Note kind="warn">
                  Your own traffic went above the cover level {status.cover_exceeded_pct.toFixed(0)}%
                  of the time. During those periods the link showed your real rate — raise the
                  floor, or use Fixed rate, which has no such gap.
                </Note>
              )}
            </Mech>

            <Mech
              tour="obf.strict" name="Fixed rate" what="Holds the link at one amount always — filler makes up whatever you are not using."
              on={strictOn} busy={busy}
              blocked={!wireOn ? "needs the protocol hidden" : null}
              costs={[
                [shaped ? `${fmtRate(capMbit)}–${fmtRate(capMax)} always` : `${fmtRate(capMbit)} always`, "warn"],
                ["caps your throughput", "bad"],
                [strictOn ? `+${Math.round((status?.strict_hold_mean_us ?? 0) / 1000)} ms typical` : "adds delay"],
              ]}
              live={strictOn
                ? `${((status?.strict_cover_share ?? 0) * 100).toFixed(0)}% filler · ${fmtCount(status?.strict_drops)} dropped`
                : "off"}
              liveWarn={status?.strict_drops > 0}
              onToggle={async () => {
                if (!strictOn) {
                  const ok = await confirm({
                    title: "Hold this link at a fixed rate?",
                    message: shaped
                      ? `The link will carry between ${fmtRate(capMbit)} and ${fmtRate(capMax)} at all times, following the “${PATTERNS.find((r) => r[0] === strictShape)?.[1]}” pattern — whether or not anyone is using it.\n\nThe cost: anything this site sends above ${fmtRate(capMbit)} is delayed, then dropped. Set the rate above what the site normally uses.`
                      : `The link will carry ${fmtRate(capMbit)} at all times, whether or not anyone is using it — so it always looks the same from outside.\n\nThe cost: anything this site sends above ${fmtRate(capMbit)} is delayed, then dropped. Set the rate above what the site normally uses.`,
                    confirmLabel: "Hold the rate", variant: "danger",
                  });
                  if (!ok) return;
                }
                settle("strictRate"); settle("strictSize");
                raw("/obf/strict", { enabled: !strictOn, rate_pps: strictRate, size_bytes: strictSize },
                  strictOn ? "Fixed rate off" : "Fixed rate on");
              }}
            >
              <Field label="Hold the link at">
                <input type="number"
                       min={rateUnit === "kbit" ? 10 : 0.1}
                       max={rateUnit === "kbit" ? 20000 : 20}
                       step={rateUnit === "kbit" ? 10 : 0.1}
                       value={rateUnit === "kbit" ? Math.round(capMbit * 1000) : +capMbit.toFixed(1)}
                       onChange={(e) => {
                         touch("strictRate");
                         const v = +e.target.value || 0;
                         const m = rateUnit === "kbit" ? v / 1000 : v;
                         const c = Math.min(20, Math.max(0.008, m));
                         setStrictRate(Math.max(1, Math.round((c * 1e6) / (strictSize * 8))));
                       }} />
                <div className="tp-seg tp-seg--tight">
                  {["kbit", "Mbit"].map((u) => (
                    <button key={u} className={`tp-seg-btn${rateUnit === u ? " is-on" : ""}`}
                            onClick={() => setRateUnit(u)}>{u}/s</button>
                  ))}
                </div>
                <button className="tp-btn tp-btn--sm" disabled={busy || !wireOn || !strictOn}
                        onClick={() => raw("/obf/strict",
                          { enabled: true, rate_pps: strictRate, size_bytes: strictSize }, "Rate updated")}>
                  Apply
                </button>
              </Field>
              <p className="tp-hint">
                Set this <strong>above</strong> what this site normally uses. Below that line it
                stops being concealment and becomes a speed limit.
              </p>

              <Field label="Rate pattern">
                <div className="tp-chips">
                  {PATTERNS.map(([key, label, why]) => (
                    <button key={key} title={why}
                            className={`tp-chip${strictShape === key ? " is-on" : ""}`}
                            disabled={busy || !wireOn}
                            onClick={() => { setStrictShape(key); settle("strictShape"); applyStrictShape(key); }}>
                      {label}
                    </button>
                  ))}
                </div>
              </Field>

              {shaped && (
                <>
                  {spark && (
                    <div className="tp-spark">
                      <svg viewBox="0 0 100 28" preserveAspectRatio="none">
                        <line x1="0" x2="100"
                              y1={26 - (capMbit / Math.max(capMax * 1.05, 0.001)) * 24}
                              y2={26 - (capMbit / Math.max(capMax * 1.05, 0.001)) * 24}
                              className="tp-spark-floor" />
                        <path d={spark} className="tp-spark-line" />
                      </svg>
                      <div className="tp-spark-key">
                        <span>the last {history.length} samples</span>
                        <span>never below {fmtRate(capMbit)}</span>
                      </div>
                    </div>
                  )}
                  <Field label="How much it varies">
                    <input type="range" min="5" max="150" step="5" value={strictSpread}
                           disabled={busy || !wireOn}
                           onChange={(e) => { touch("strictSpread"); setStrictSpread(+e.target.value); }}
                           onMouseUp={() => applyStrictShape(strictShape)}
                           onTouchEnd={() => applyStrictShape(strictShape)} />
                    <span className="tp-val">+{strictSpread}%</span>
                  </Field>
                  <Field label="Pick a new rate every">
                    <input type="number" min="2" max="600" value={strictPeriod}
                           onChange={(e) => { touch("strictPeriod"); setStrictPeriod(Math.min(600, Math.max(2, +e.target.value || 2))); }} />
                    <span className="tp-unit">seconds</span>
                    <button className="tp-btn tp-btn--sm" disabled={busy || !wireOn}
                            onClick={() => applyStrictShape(strictShape)}>Apply</button>
                  </Field>
                  <p className="tp-hint">
                    The rate you set is the <strong>floor</strong> — a pattern only ever adds on
                    top. It can raise the bandwidth you spend; it can never slow your traffic
                    more than the rate you already chose.
                  </p>
                </>
              )}

              {strictOn && status?.strict_drops > 0 && (
                <Note kind="warn">
                  {fmtCount(status.strict_drops)} of your packets have been dropped — this site
                  is trying to send more than {fmtRate(capMbit)}. Raise the rate, or turn this off.
                </Note>
              )}
              {strictOn && status?.strict_oversize > 0 && (
                <p className="tp-hint">
                  {fmtCount(status.strict_oversize)} packets were too big for the {strictSize}-byte
                  size and went out at their own length. Those few stand out against the rest.
                </p>
              )}
            </Mech>

            <Mech
              tour="obf.jitter" name="Timing randomisation" what="Delays each packet a random amount, so the rhythm stops matching the app."
              on={jitterOn} busy={busy}
              blocked={!wireOn ? "needs the protocol hidden" : strictOn ? "superseded by Fixed rate" : null}
              costs={[[`+0–${jitterMs} ms`, "warn"], ["costs reorder budget"]]}
              live={jitterOn ? `up to ${status?.jitter_max_ms ?? jitterMs} ms` : "off"}
              onToggle={() => raw("/obf/jitter", { enabled: !jitterOn, max_ms: jitterMs },
                jitterOn ? "Timing randomisation off" : "Timing randomisation on")}
            >
              <Field label="Delay each packet up to">
                <input type="number" min="1" max="25" value={jitterMs}
                       onChange={(e) => { touch("jitterMs"); setJitterMs(Math.min(25, Math.max(1, +e.target.value || 1))); }} />
                <span className="tp-unit">ms</span>
                <button className="tp-btn tp-btn--sm" disabled={busy || !wireOn || !jitterOn || strictOn}
                        onClick={() => raw("/obf/jitter", { enabled: true, max_ms: jitterMs }, "Delay updated")}>
                  Apply
                </button>
              </Field>
              <p className="tp-hint">
                Capped at 25 ms — past that the far end gives up on delayed packets, and the cost
                turns into loss instead of latency.
              </p>
            </Mech>

            <Mech
              tour="obf.anchors" name="Spread across exits" what="Sends each connection out from a different place, so no one exit sees the site."
              on={anchorsOn} busy={busy}
              blocked={!wireOn ? "needs the protocol hidden" : exitsBlocked}
              costs={[["needs 2+ exits"], ["adds distance to some flows", "warn"]]}
              live={anchorsOn
                ? `${readyCount} exits · behaving like ${effAnchors.toFixed(1)} by volume${balancing ? " · balancing" : ""}`
                : `${readyCount} reachable`}
              liveWarn={anchorsOn && effAnchors < readyCount - 0.5}
              onToggle={async () => {
                if (!anchorsOn && !(await confirmAnchorCoverage())) return;
                raw("/obf/anchors", { enabled: !anchorsOn },
                  anchorsOn ? "Exit spreading off" : "Exit spreading on");
              }}
              action={anchorsOn && (
                <button className="tp-btn tp-btn--sm" disabled={busy}
                        onClick={() => raw("/obf/anchors/rotate", {}, "Exit choice re-drawn")}>
                  <FiRefreshCw size={12} /> Re-draw exits
                </button>
              )}
            >
              <p className="tp-hint">
                A connection is pinned to one exit for its life — it cannot be split, because each
                exit presents its own public address. Re-drawing moves <em>new</em> connections;
                existing ones keep their exit until they close.
                {frozenFlows > 0 && (
                  <> Right now {frozenFlows} {frozenFlows > 1 ? "connections have" : "connection has"}{" "}
                  outlived a re-draw, so {frozenFlows > 1 ? "those exits keep" : "that exit keeps"} showing
                  the same traffic until {frozenFlows > 1 ? "they close" : "it closes"}.</>
                )}
                {balancing && (
                  <> The even draw is currently steering new connections toward the exits carrying
                  least, so the split evens out by volume rather than by connection count.</>
                )}
              </p>

              {anchorsOn && readyCount >= 2 && (
                <>
                  {/* The random-vs-balanced dial. Only meaningful while the draw is
                      even — an explicit split below is the operator's own choice
                      and the correction deliberately stands aside for it. */}
                  {!appliedWeights && (
                    <>
                      <Field label="Even out the volume">
                        <input type="range" min={biasMin * 10} max={biasMax * 10} step="1"
                               value={Math.round(bias * 10)}
                               disabled={busy || !wireOn}
                               onChange={(e) => { touch("bias"); setBias(+e.target.value / 10); }} />
                        <span className="tp-val">
                          {bias <= biasMin ? "purely random" : `${bias.toFixed(1)}×`}
                        </span>
                        <button className="tp-btn tp-btn--sm" disabled={busy || !wireOn}
                                onClick={() => { settle("bias"); raw("/obf/anchors/balance", { max_bias: bias }, "Balance updated"); }}>
                          Apply
                        </button>
                      </Field>
                      <p className="tp-hint">
                        {bias <= biasMin
                          ? "Every exit is equally likely, so a few heavy connections can end up owning one of them."
                          : `New connections lean toward the exits carrying least. One exit can end up with up to ${Math.round(biasRatio)}× another's share of connections — which is also how predictable the next choice becomes to anyone who can see load.`}
                        {" "}Existing connections never move, so a skew corrects as they close, not now.
                      </p>
                    </>
                  )}

                  <Field label="Balance across exits">
                    <span className={`tp-eff${(pendingEff ?? effAnchors) < readyCount - 0.35 ? " warn" : ""}`}>
                      {pendingEff != null
                        ? `would behave like ${pendingEff.toFixed(2)} of ${readyCount}`
                        : `behaves like ${effAnchors.toFixed(2)} of ${readyCount} by volume`}
                    </span>
                  </Field>
                  {readyList.map((id) => (
                    <Field key={id} label={id} mono>
                      <input type="range" min="0" max="100" step="1" value={weightPct(id)}
                             disabled={busy || !wireOn}
                             onChange={(e) => setWeight(id, +e.target.value)} />
                      <span className="tp-val">{weightPct(id)}%</span>
                    </Field>
                  ))}
                  <Field label={`total ${readyList.reduce((t, a) => t + weightPct(a), 0)}%`}>
                    <button className="tp-btn tp-btn--sm" disabled={busy || !wireOn || !weights}
                            onClick={() => applyWeights(weights)}>Apply</button>
                    <button className="tp-btn tp-btn--sm" disabled={busy || !wireOn}
                            onClick={() => { setWeights(null); applyWeights(null); }}>Even split</button>
                  </Field>
                  <p className="tp-hint">
                    An even split is the stronger setting: each exit takes the same share, and that
                    share says nothing about the rest. Favouring one buys latency and costs
                    diversity. Applies to <em>new</em> connections only.
                  </p>
                </>
              )}

              {anchorsOn && readyCount < 2 && (
                <Note kind="warn">
                  Only {readyCount} exit is reachable, so there is nothing to spread across.
                  Traffic is all going to one place.
                </Note>
              )}
            </Mech>

            <Mech
              tour="obf.rotation" name="Automatic re-keying" what="Re-picks the link and exit schedules on a randomised interval, so nothing learned stays true."
              on={mtdOn} busy={busy}
              blocked={!wireOn ? "needs the protocol hidden" : null}
              costs={[["free — nothing is negotiated"]]}
              live={mtdOn
                ? `every ~${fmtDuration(mtdPeriod)} · ${fmtCount(status?.mtd_rotations)} so far${
                    status?.mtd_next_in_sec != null ? ` · next in ${fmtDuration(status.mtd_next_in_sec)}` : ""}`
                : "off"}
              onToggle={() => raw("/obf/mtd", { enabled: !mtdOn, period_sec: mtdPeriod },
                mtdOn ? "Automatic re-keying off" : "Automatic re-keying on")}
            >
              <Field label="Re-key roughly every">
                <input type="number" min="60" max="86400" step="60" value={mtdPeriod}
                       onChange={(e) => { touch("mtdPeriod"); setMtdPeriod(Math.min(86400, Math.max(60, +e.target.value || 60))); }} />
                <span className="tp-unit">seconds</span>
                <button className="tp-btn tp-btn--sm" disabled={busy || !wireOn || !mtdOn}
                        onClick={() => raw("/obf/mtd", { enabled: true, period_sec: mtdPeriod }, "Re-key interval updated")}>
                  Apply
                </button>
              </Field>
              <p className="tp-hint">
                Costs nothing — neither schedule is shared with the far end, so re-keying cannot
                interrupt traffic. Connections already running keep their exit until they close.
              </p>
            </Mech>
          </div>
        )}
      </section>
    </div>
  );
}

/* ── Pieces ──────────────────────────────────────────────────────────────── */

function StepHead({ n, title, sub }) {
  return (
    <div className="tp-step">
      <span className="tp-step-n">{n}</span>
      <div><b>{title}</b><span>{sub}</span></div>
    </div>
  );
}

function Alert({ kind, title, children }) {
  return (
    <div className={`tp-alert tp-alert--${kind}`}>
      <FiAlertTriangle size={16} />
      <div><b>{title}</b><div>{children}</div></div>
    </div>
  );
}

function Note({ kind, children }) {
  return (
    <div className={`tp-note${kind ? ` tp-note--${kind}` : ""}`}>
      <FiAlertTriangle size={13} /><span>{children}</span>
    </div>
  );
}

function Bar({ name, pct, hot, mono }) {
  return (
    <div className="tp-bar-row">
      <span className={`tp-bar-name${mono ? " mono" : ""}`}>{name}</span>
      <span className="tp-bar">
        <span className="tp-bar-fill" style={{ width: `${pct}%`, background: hot ? "#F59E0B" : "#3B82F6" }} />
      </span>
      <span className="tp-bar-val">{pct.toFixed(0)}%</span>
    </div>
  );
}

function Stat({ label, value, warn }) {
  return (
    <div className="tp-stat">
      <div className="tp-stat-label">{label}</div>
      <div className={`tp-stat-value${warn ? " warn" : ""}`}>{value}</div>
    </div>
  );
}

function Field({ label, mono, children }) {
  return (
    <div className="tp-field">
      <span className={`tp-field-label${mono ? " mono" : ""}`}>{label}</span>
      {children}
    </div>
  );
}

/* One anatomy for every mechanism: name, one line of what it does, one toggle,
   the cost it spends, what it is doing right now, and its parameters behind a
   disclosure. The panel used to use seven control idioms across eight blocks,
   three of which meant on/off. `blocked` carries an interlock as visible state
   rather than as an error after the click. */
function Mech({ tour, name, what, on, busy, blocked, costs = [], live, liveWarn, onToggle, children, action }) {
  const [open, setOpen] = useState(false);
  return (
    /* data-tour-blocked publishes the interlock, so a walkthrough stopping here
       can say why the switch will not move instead of telling the operator to
       set something the panel is refusing to let them set. */
    <div data-tour={tour} data-tour-blocked={blocked || undefined}
         className={`tp-mech${on ? " is-on" : ""}${blocked ? " is-blocked" : ""}`}>
      <div className="tp-mech-top">
        <button type="button" className="tp-mech-name" disabled={!children}
                aria-expanded={children ? open : undefined}
                onClick={() => children && setOpen((v) => !v)}>
          <span className="tp-mech-title">
            {name}{children && <FiChevronDown size={13} className={open ? "rot" : ""} />}
          </span>
          <span className="tp-mech-what">{what}</span>
        </button>

        {blocked && <span className="tp-mech-blocked">{blocked}</span>}

        <button type="button" className={`tp-switch${on ? " is-on" : ""}`}
                role="switch" aria-checked={on} aria-label={`${on ? "Turn off" : "Turn on"} ${name}`}
                disabled={busy || !!blocked} title={blocked || undefined} onClick={onToggle}>
          <span className="tp-switch-knob" />
        </button>
      </div>

      <div className="tp-mech-foot">
        {costs.map(([text, tone]) => (
          <span key={text} className={`tp-cost${tone ? ` ${tone}` : ""}`}>{text}</span>
        ))}
        {live != null && (
          <span className={`tp-mech-live${on ? (liveWarn ? " warn" : "") : " off"}`}>{live}</span>
        )}
      </div>

      {children && open && <div className="tp-mech-body">{children}</div>}
      {action && <div className="tp-mech-acts">{action}</div>}
    </div>
  );
}
